import { trackEvent } from "@/lib/audit";

export type TenantPlan = "starter" | "growth" | "enterprise";
export type TenantStatus = "active" | "suspended";

export type TenantRole =
  | "tenant_owner"
  | "tenant_admin"
  | "tenant_marketing"
  | "tenant_analyst"
  | "tenant_viewer";

export interface TenantUser {
  id: string;
  email: string;
  name: string;
  role: TenantRole;
  invited_at: string;
}

export interface TenantPermissions {
  create_campaigns: boolean;
  view_reports: boolean;
  export_data: boolean;
  manage_users: boolean;
  edit_branding: boolean;
}

export interface TenantBranding {
  logo_url: string;
  primary_color: string; // hex
  default_lang: "ES" | "EN" | "PT";
  legal_text: string;
}

export interface TenantLicense {
  license_type: TenantPlan;
  monthly_fee: number;
  contract_start: string;
  contract_end: string;
  seats: number;
  feature_flags: string[];
}

export interface Tenant {
  tenant_id: string;
  tenant_name: string;
  slug: string;
  domain: string;
  country: string;
  currency: string;
  timezone: string;
  status: TenantStatus;
  branding: TenantBranding;
  license: TenantLicense;
  permissions: TenantPermissions;
  users: TenantUser[];
  created_at: string;
  updated_at: string;
}

const STORAGE_KEY = "ss26.tenants.v1";
const IMPERSONATION_KEY = "ss26.tenants.impersonation";

function nowIso() {
  return new Date().toISOString();
}

function defaultPermissions(): TenantPermissions {
  return {
    create_campaigns: true,
    view_reports: true,
    export_data: true,
    manage_users: true,
    edit_branding: true,
  };
}

function seed(): Tenant[] {
  const base: Omit<Tenant, "tenant_id" | "tenant_name" | "slug" | "domain" | "branding" | "license">[] = [];
  void base;
  const make = (
    name: string,
    slug: string,
    domain: string,
    country: string,
    currency: string,
    color: string,
    plan: TenantPlan,
    fee: number,
  ): Tenant => ({
    tenant_id: `tenant_${slug}`,
    tenant_name: name,
    slug,
    domain,
    country,
    currency,
    timezone: "America/Mexico_City",
    status: "active",
    branding: {
      logo_url: "",
      primary_color: color,
      default_lang: "ES",
      legal_text: `© ${new Date().getFullYear()} ${name}. All rights reserved.`,
    },
    license: {
      license_type: plan,
      monthly_fee: fee,
      contract_start: nowIso(),
      contract_end: new Date(Date.now() + 365 * 86400 * 1000).toISOString(),
      seats: plan === "enterprise" ? 50 : plan === "growth" ? 15 : 5,
      feature_flags: ["campaigns", "reports", "ai_scoped"],
    },
    permissions: defaultPermissions(),
    users: [
      {
        id: `u_${slug}_owner`,
        email: `owner@${domain}`,
        name: `${name} Owner`,
        role: "tenant_owner",
        invited_at: nowIso(),
      },
    ],
    created_at: nowIso(),
    updated_at: nowIso(),
  });
  return [
    make("Univision", "univision", "univision.com", "US", "USD", "#0033A0", "enterprise", 12000),
    make("Betting Partner Demo", "betting-partner", "casaapuestas.com", "MX", "MXN", "#E4002B", "growth", 4500),
    make("Coca-Cola", "coca-cola", "coca-cola.com", "GLOBAL", "USD", "#F40009", "enterprise", 18000),
    make("Gatorade", "gatorade", "gatorade.com", "GLOBAL", "USD", "#FF6B00", "growth", 6000),
  ];
}

function read(): Tenant[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const seeded = seed();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
      return seeded;
    }
    const parsed = JSON.parse(raw) as Tenant[];
    // PR-12.1: ensure required demo tenants always exist (heal old caches).
    const required = seed();
    const slugs = new Set(parsed.map((t) => t.slug));
    const merged = [...parsed];
    for (const r of required) if (!slugs.has(r.slug)) merged.push(r);
    if (merged.length !== parsed.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    }
    return merged;
  } catch {
    return seed();
  }
}

function write(list: Tenant[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent("ss26:tenants-changed"));
  } catch {
    /* ignore */
  }
}

export const tenantStore = {
  list(): Tenant[] {
    return read();
  },
  get(id: string): Tenant | null {
    const list = read();
    // Accept tenant_id, slug, or aliases (e.g. "betting-demo" → "betting-partner").
    const aliasMap: Record<string, string> = {
      "betting-demo": "betting-partner",
      tenant_betting_demo: "tenant_betting-partner",
      "tenant_betting-demo": "tenant_betting-partner",
    };
    const needle = aliasMap[id] ?? id;
    return (
      list.find((t) => t.tenant_id === needle) ??
      list.find((t) => t.slug === needle) ??
      list.find((t) => t.tenant_id === `tenant_${needle}`) ??
      null
    );
  },
  create(input: Partial<Tenant> & { tenant_name: string; slug: string }): Tenant {
    const existing = read();
    const t: Tenant = {
      tenant_id: `tenant_${input.slug}`,
      tenant_name: input.tenant_name,
      slug: input.slug,
      domain: input.domain ?? `${input.slug}.com`,
      country: input.country ?? "US",
      currency: input.currency ?? "USD",
      timezone: input.timezone ?? "America/Mexico_City",
      status: "active",
      branding: input.branding ?? {
        logo_url: "",
        primary_color: "#3B82F6",
        default_lang: "ES",
        legal_text: `© ${new Date().getFullYear()} ${input.tenant_name}.`,
      },
      license: input.license ?? {
        license_type: "starter",
        monthly_fee: 1500,
        contract_start: nowIso(),
        contract_end: new Date(Date.now() + 365 * 86400 * 1000).toISOString(),
        seats: 5,
        feature_flags: ["campaigns", "reports"],
      },
      permissions: input.permissions ?? defaultPermissions(),
      users: input.users ?? [],
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    write([t, ...existing]);
    void trackEvent("tenant_created", { tenant_id: t.tenant_id, name: t.tenant_name });
    return t;
  },
  update(id: string, patch: Partial<Tenant>): Tenant | null {
    const list = read();
    const idx = list.findIndex((t) => t.tenant_id === id);
    if (idx < 0) return null;
    const next = { ...list[idx], ...patch, updated_at: nowIso() };
    list[idx] = next;
    write(list);
    void trackEvent("tenant_updated", { tenant_id: id });
    return next;
  },
  suspend(id: string) {
    this.update(id, { status: "suspended" });
    void trackEvent("tenant_suspended", { tenant_id: id });
  },
  reactivate(id: string) {
    this.update(id, { status: "active" });
    void trackEvent("tenant_reactivated", { tenant_id: id });
  },
  remove(id: string) {
    write(read().filter((t) => t.tenant_id !== id));
    void trackEvent("tenant_deleted", { tenant_id: id });
  },
  inviteUser(id: string, user: Omit<TenantUser, "id" | "invited_at">) {
    const t = this.get(id);
    if (!t) return;
    const u: TenantUser = {
      ...user,
      id: `u_${id}_${Date.now()}`,
      invited_at: nowIso(),
    };
    this.update(id, { users: [...t.users, u] });
    void trackEvent("tenant_user_invited", { tenant_id: id, role: user.role });
  },
  startImpersonation(id: string) {
    try {
      localStorage.setItem(IMPERSONATION_KEY, id);
      void trackEvent("tenant_impersonation_started", { tenant_id: id });
    } catch {
      /* ignore */
    }
  },
  endImpersonation() {
    try {
      const id = localStorage.getItem(IMPERSONATION_KEY);
      localStorage.removeItem(IMPERSONATION_KEY);
      if (id) void trackEvent("tenant_impersonation_ended", { tenant_id: id });
    } catch {
      /* ignore */
    }
  },
  currentImpersonation(): string | null {
    try {
      return typeof window === "undefined" ? null : localStorage.getItem(IMPERSONATION_KEY);
    } catch {
      return null;
    }
  },
};
